import { forwardRef } from '@heroui/system';
import { MdOutlineCalendarToday } from 'react-icons/md';
import { DateRangePicker, DateRangePickerProps, extendVariants } from '@heroui/react';
import { isEmpty } from 'lodash-es';

export const CustomRangeDatePicker = extendVariants(DateRangePicker, {
  variants: {
    color: {
      default: {
        base: '[&>div]:border [&>div]:border-solid [&>div]:rounded-lg [&>div]:bg-base-60',
        selectorButton: 'text-base-5',
        label: 'text-base-20 tmdx-input__label group-focus-within:text-base-10 group-data-[invalid=true]:text-danger!',
        popoverContent: 'bg-base-60 shadow-xl',
        inputWrapper: 'bg-base-60',
        segment: 'slashed-zero focus:bg-base-40',
      },
      primary: {
        label: 'text-base-20 tmdx-input__label group-focus-within:text-base-10 group-data-[invalid=true]:text-danger!',
        selectorButton: 'text-base-5',
        inputWrapper: 'bg-base-50',
        segment: 'slashed-zero focus:bg-base-40',
      },
    },
    size: {
      sm: {
        label: 'text-s-r!',
        calendarContent: 'min-w-65',
      },
      md: {
        label: 'text-s-r!',
        calendarContent: 'min-w-65',
      },
      lg: {
        base: 'h-14',
        label: 'text-s-r!',
        calendarContent: 'min-w-65',
      },
    },
  },
  defaultVariants: {
    color: 'default',
    size: 'lg',
  },
});

export interface CustomRangeDatePickerProps extends Omit<DateRangePickerProps, 'ref' | 'color' | 'size'> {
  color?: 'default' | 'primary';
  size?: 'sm' | 'md' | 'lg';
}

const CustomRangeDatePickerWrapper = forwardRef<'input', CustomRangeDatePickerProps>((props, ref) => {
  const {
    calendarWidth = 260,
    value,
    label,
    hourCycle = 24,
    hideTimeZone = true,
    granularity = 'day',
    onChange,
    ...restProps
  } = props;
  const segmentTextStyle = isEmpty(value) ? 'text-base-30!' : 'text-base-5!';

  return (
    <CustomRangeDatePicker
      ref={ref}
      selectorIcon={<MdOutlineCalendarToday size={20} />}
      label={label}
      value={value}
      calendarWidth={calendarWidth}
      classNames={{
        base: [
          'tmdx-input',
          'tmdx-calendar',
          'tmdx-input-date group',
          'tmdx-input-date--range',
          !label ? 'noLabel' : 'hasLabel',
          'overflow-hidden',
        ],
        inputWrapper: 'rounded-lg',
        calendar: 'tmdx-calendar',
        popoverContent: 'tmdx-calendar__popover',
        calendarContent: 'tmdx-calendar__content',
        timeInputLabel: 'text-s-m! text-black w-16',
        timeInput: 'tmdx-daterange__time-input bg-base-60 gap-x-2 px-4 text-base-5',
        segment: segmentTextStyle,
        separator: segmentTextStyle,
      }}
      onChange={onChange}
      hourCycle={hourCycle}
      hideTimeZone={hideTimeZone}
      granularity={granularity}
      {...restProps}
    />
  );
});

CustomRangeDatePickerWrapper.displayName = 'CustomRangeDatePicker';

export default CustomRangeDatePickerWrapper;
